<template>
    <div class="cms-page">
        <template v-if="isLoadingCmsPage">
            <div class="cms-page__vehicles-loader vehicles">
                <div class="vehicles__content">
                    <div class="vehicles__search-box">
                        <VehicleSearchForm :loading="true" class="vehicles__search-form" />
                    </div>

                    <div class="vehicles__list-wrap">
                        <VehicleList class="vehicles__loading" :loading-count="24" />
                    </div>
                </div>
            </div>
        </template>
        <template v-else-if="cmsPage">
            <BrandOverview v-if="cmsPage.template === TEMPLATE_BRAND_OVERVIEW" class="cms-page__template" />
            <Vehicles v-else-if="cmsPage.template === TEMPLATE_VEHICLES" class="cms-page__template" />

            <MainFooter />
        </template>
    </div>
</template>

<router>
{
    meta: {
        hideGettingStartedProgressBar: true
    }
}
</router>

<script>
import { mapGetters } from 'vuex';

import { actionTypes as cmsActions } from '~/store/cms';
import { actionTypes as navigationActions } from '~/store/navigation';

import {
    ASSET_SUB_TYPE_MOTORHOME,
    ASSET_SUB_TYPE_CAMPERVAN,
    ASSET_SUB_TYPE_CARAVAN,
    ASSET_SUB_TYPE_OTHER_CAMPER,
} from '~/config/assetTypes';

import { MAX_ALLOWED_DRIVING_LICENSE_CLASS, RENTABLE_OUTSIDE_OF_SWITZERLAND } from '~/config/filterKeys';
import { buildQuery } from '~/helpers/queryParser';

import Vehicles from '~/components/cms-templates/Vehicles.vue';
import VehicleSearchForm from '~/components/vehicles/VehicleSearchForm.vue';
import VehicleList from '~/components/vehicles/VehicleList.vue';

import allLocales from '~/config/locales.json';
import BrandOverview from '~/components/cms-templates/BrandOverview';
import MainFooter from '~/components/layout/MainFooter';
import { TEMPLATE_VEHICLES, TEMPLATE_BRAND_OVERVIEW } from '~/config/pageTemplateTypes';

export default {
    components: {
        MainFooter,
        BrandOverview,
        Vehicles,
        VehicleSearchForm,
        VehicleList,
    },
    async asyncData(context) {
        let baseUrl;

        if (process.client) {
            baseUrl = window.location.origin;
        } else {
            const protocol = context.req.connection.encrypted ? 'https' : 'http';
            baseUrl = `${protocol}://${context.req.headers.host}`;
        }

        const route = context.route;
        const pagePath = context.$pageService.removeAnyLocalePrefix(route.path, allLocales);
        const itemType = route.query.itemTypes || route.query.itemTyper;

        // Handling redirects based on queries from old platform (doing it here because old path for search page is same as now for french locale)
        if (context.app.i18n.locale === 'fr' && itemType) {
            if (itemType === 'Caravan') {
                return context.redirect(
                    301,
                    context.localePath({ path: context.app.i18n.t('common.caravans_page_path') })
                );
            } else if (itemType === 'MotorHome') {
                return context.redirect(
                    301,
                    context.localePath({ path: context.app.i18n.t('common.campervans_page_path') })
                );
            }
        }

        const setupRouteParams = page => {
            if (Array.isArray(page.translations)) {
                const routeParams = context.$pageService.getRouteParams(page, allLocales, context.i18n.locale);

                context.store.dispatch(navigationActions.SET_ROUTE_PARAMS, { routeParams, i18n: context.i18n });
            }
        };

        if (pagePath === context.store.getters.currentCmsPath) {
            const { getCmsPageStructuredData } = context.store.getters;

            setupRouteParams(context.store.getters.cmsPage);

            return {
                structuredData: getCmsPageStructuredData(baseUrl),
            };
        }

        await context.store
            .dispatch(cmsActions.CLEAR)
            .then(() => {
                context.store.dispatch(cmsActions.SET_LOADING, true);

                return context.store.dispatch(cmsActions.FETCH_PAGE, pagePath);
            })
            .then(() => {
                const page = context.store.getters.cmsPage;

                setupRouteParams(page);

                return Promise.resolve(page);
            })
            .catch(error => {
                if (error.message === '404 page not found') {
                    return context.error({ statusCode: 404, message: error.message });
                } else {
                    context.$sentry.captureException(error);
                }

                throw error;
            })
            .finally(() => {
                context.store.dispatch(cmsActions.SET_LOADING, false);
            });

        return {
            structuredData: context.store.getters.getCmsPageStructuredData(baseUrl),
        };
    },
    data() {
        return {
            ASSET_SUB_TYPE_MOTORHOME,
            ASSET_SUB_TYPE_CAMPERVAN,
            ASSET_SUB_TYPE_CARAVAN,
            ASSET_SUB_TYPE_OTHER_CAMPER,
            TEMPLATE_VEHICLES,
            TEMPLATE_BRAND_OVERVIEW,
        };
    },
    head() {
        const headObj = {
            title: this.pageTitle,
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: this.pageDescription,
                },
                { hid: 'og-title', property: 'og:title', content: this.pageTitle },
                { hid: 'og-description', property: 'og:description', content: this.pageDescription },
            ],
            script: [],
        };

        if (this.structuredData) {
            headObj.script.push({ type: 'application/ld+json', json: this.structuredData });
        }

        return headObj;
    },
    computed: {
        ...mapGetters([
            'cmsPage',
            'isLoadingCmsPage',
            'isLoggedIn',
            'isRenter',
            'userCanBuyBaloiseInsurance',
            'modifiedAutoActivatedFilters',
            'user',
        ]),
        pageTitle() {
            if (this.cmsPage) {
                return this.cmsPage.metaTitle || this.cmsPage.heading;
            }

            return this.$t('pages.vehicles.seo_page_title');
        },
        pageDescription() {
            if (!this.cmsPage) {
                return this.$t('pages.vehicles.default_seo_page_description');
            }

            if (this.cmsPage.metaDescription) {
                return this.cmsPage.metaDescription;
            }

            const { vehicleTypes, location } = this.cmsPage.searchSettings;

            if (!vehicleTypes || !vehicleTypes.length) {
                return this.$t('pages.vehicles.default_seo_page_description');
            }

            let vehicleLabel, vehicleLabelPlural;

            if (vehicleTypes.includes(ASSET_SUB_TYPE_CAMPERVAN)) {
                vehicleLabel = this.$tc('common.campervan', 1);
                vehicleLabelPlural = this.$tc('common.campervan', 2);
            } else if (vehicleTypes.includes(ASSET_SUB_TYPE_MOTORHOME)) {
                vehicleLabel = this.$tc('common.motorhome', 1);
                vehicleLabelPlural = this.$tc('common.motorhome', 2);
            } else if (vehicleTypes.includes(ASSET_SUB_TYPE_OTHER_CAMPER)) {
                vehicleLabel = this.$tc('common.other_camper', 1);
                vehicleLabelPlural = this.$tc('common.other_camper', 2);
            } else {
                vehicleLabel = this.$tc('common.caravan', 1);
                vehicleLabelPlural = this.$tc('common.caravan', 2);
            }

            const descriptionArgs = {
                vehicleType: vehicleLabel,
                vehicleTypePlural: vehicleLabelPlural,
                location: location?.name,
            };

            if (location?.name) {
                return this.$t('pages.vehicles.seo_page_desc_with_vehicle_and_location', descriptionArgs);
            }

            return this.$t('pages.vehicles.seo_page_desc_with_vehicle', descriptionArgs);
        },
    },
    watch: {
        isLoggedIn: {
            handler() {
                this.applyDefaultRenterFilters();
            },
            immediate: true,
        },
    },
    methods: {
        /**
         * Apply default filters for logged in renters who meet certain criteria
         * If user made changes to those filters we don't override them during the session
         *
         * @returns {void}
         */
        applyDefaultRenterFilters() {
            if (!this.isLoggedIn || !this.isRenter) {
                return;
            }

            const defaultFilters = {};

            if (
                this.user.drivingLicenseClass &&
                this.modifiedAutoActivatedFilters[MAX_ALLOWED_DRIVING_LICENSE_CLASS] === undefined
            ) {
                defaultFilters[MAX_ALLOWED_DRIVING_LICENSE_CLASS] = this.user.drivingLicenseClass;
            }

            if (
                this.user.country &&
                !this.userCanBuyBaloiseInsurance &&
                this.modifiedAutoActivatedFilters[RENTABLE_OUTSIDE_OF_SWITZERLAND] === undefined
            ) {
                defaultFilters[RENTABLE_OUTSIDE_OF_SWITZERLAND] = true;
            }

            if (Object.keys(defaultFilters).length > 0) {
                this.dispatchEvent(defaultFilters);
            }

            const queryWithDefaultFilters = buildQuery(this.$route.query, defaultFilters);
            this.$router.replace({ query: queryWithDefaultFilters });
        },
        dispatchEvent(filters) {
            const filtersKeys = Object.keys(filters);
            const mappedFilters = filtersKeys.map(entry => `${entry}: ${filters[entry]}`);

            if (process.client && this.$gtm.enabled()) {
                window.dataLayer?.push({
                    event: 'automatic_filters_applied',
                    action: filtersKeys.join(', '),
                    label: mappedFilters.join(', '),
                    locale: this.$i18n.locale,
                });
            }
        },
    },
};
</script>

<style lang="scss">
.cms-page-loading {
    margin-top: 50px;
}
</style>
